/** The pending reset carried by a successful passwordless verify response, if any. */
export function pendingResetFromGrant(grant) {
    const token = grant?.password_reset_token;
    if (!token)
        return null;
    return { token, required: false };
}
/**
 * The reset token from a sign-in refused with 403 FORCE_PASSWORD_RESET_REQUIRED
 * (the user's manager requires a new password; no session was started), or
 * null for any other failure.
 */
export function forcedResetTokenFrom(response) {
    const body = response?.errorBody;
    if (body?.code !== 'FORCE_PASSWORD_RESET_REQUIRED' || typeof body.reset_token !== 'string' || !body.reset_token)
        return null;
    return body.reset_token;
}
/**
 * Reads the reset token the API puts in the URL fragment after a
 * "Forgot password?" sign-in link (#password_reset_token=…&password_reset_required=1).
 * The fragment never reaches a server; callers clear it from the address bar.
 */
export function pendingResetFromHash(hash) {
    if (!hash)
        return null;
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const token = params.get('password_reset_token');
    if (!token)
        return null;
    return { token, required: params.get('password_reset_required') === '1' };
}
/** Removes the reset keys from a URL fragment, keeping anything else. Returns '' when nothing is left. */
export function stripPasswordResetFromHash(hash) {
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    params.delete('password_reset_token');
    params.delete('password_reset_required');
    const rest = params.toString();
    return rest ? `#${rest}` : '';
}
/**
 * The account password rules (the API's ValidatePasswordStrength): 8-128
 * characters with an uppercase letter, a lowercase letter, a digit and a
 * symbol. Returns a message for the first rule broken, or null.
 */
export function newPasswordProblem(password) {
    if (password.length < 8)
        return 'Use at least 8 characters';
    if (password.length > 128)
        return 'Use 128 characters or fewer';
    if (!/[A-Z]/.test(password))
        return 'Add an uppercase letter';
    if (!/[a-z]/.test(password))
        return 'Add a lowercase letter';
    if (!/[0-9]/.test(password))
        return 'Add a number';
    if (!/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password))
        return 'Add a symbol, like ! @ # $ or %';
    return null;
}
/**
 * The user a reset token was issued to (its user_id claim), or null if it
 * cannot be read. Only for routing in the UI; the API verifies the token.
 */
export function resetTokenUserId(token) {
    const payload = token.split('.')[1];
    if (!payload)
        return null;
    try {
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
        const claims = JSON.parse(atob(padded));
        return typeof claims?.user_id === 'string' ? claims.user_id : null;
    }
    catch {
        return null;
    }
}
